import React, { useRef, useCallback, useState, useEffect } from 'react';
import { usePlan } from './PlanContext';
import Seat from './Seat';
import { computeSeatPositions, SITABLE_TYPES, SEAT_SIZE, pxToM, formatDistance } from './seatUtils';
import { Camera, DoorOpen } from 'lucide-react';

const DRAG_THRESHOLD = 4;

const DECOR_ICONS = {
  photo:   <Camera size={16} color="#F4A7B2" />,
  welcome: <DoorOpen size={16} color="#1F9D7A" />,
};

function tableStyle(type, w, h, selected, brandColor) {
  const gold  = brandColor || '#D4AF37';
  const fill  = `${gold}12`;
  const base  = {
    position:   'absolute',
    width:      w,
    height:     h,
    display:    'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    transition: 'box-shadow .15s, transform .15s',
    boxShadow:  selected ? `0 0 0 3px ${gold}, 0 4px 16px rgba(0,0,0,.12)` : '0 2px 8px rgba(0,0,0,.08)',
    userSelect: 'none',
    cursor:     'grab',
  };
  if (type === 'round' || type === 'oval')
    return { ...base, borderRadius: type === 'round' ? '50%' : '50%/38%',
      border: `2.5px solid ${gold}`, background: fill };
  if (type === 'honor')
    return { ...base, borderRadius: 4, border: `2.5px solid ${gold}`, background: `${gold}22` };
  if (type === 'dj')
    return { ...base, borderRadius: 4, background: '#0B1E20', border: `2px solid #5DBCBF`, color: '#5DBCBF' };
  if (type === 'dance')
    return { ...base, borderRadius: 4,
      background: 'repeating-linear-gradient(45deg,transparent,transparent 8px,rgba(93,188,191,.06) 8px,rgba(93,188,191,.06) 16px)',
      border: '2px dashed #5DBCBF' };
  if (type === 'stage')
    return { ...base, borderRadius: 4, background: 'rgba(212,175,55,.1)', border: `2px solid ${gold}` };
  return { ...base, borderRadius: 4, border: `2px solid ${gold}`, background: fill };
}

export default function TableElement({ table }) {
  const {
    selectedId, setSelectedId,
    setOptimisticPos, commitPosition,
    assignMode, assignSeat, unassignSeat,
    invites, planData,
    pxPerM, roomX0, roomY0, roomPxW, roomPxH,
    resolvedTables,
    showDistances,
    snapPx,
  } = usePlan();

  const drag     = useRef({ active: false });
  const selected = selectedId === table.id;
  const brandColor = planData?.brand_color || '#D4AF37';

  const w = table.w || 80;
  const h = table.h || 80;
  const rotation = table.rotation || 0;

  const [nearestDist, setNearestDist] = useState(null); // distance en mètres

  // ── Distance à la table la plus proche ───────────────────────────────────
  function computeNearestDist(curX, curY) {
    let minDist = Infinity;
    for (const t of resolvedTables) {
      if (t.id === table.id) continue;
      const tw = t.w || 80, th = t.h || 80;
      // Distance bord à bord simplifiée
      const dx = Math.max(0, Math.abs((curX + w/2) - (t.x + tw/2)) - (w/2 + tw/2));
      const dy = Math.max(0, Math.abs((curY + h/2) - (t.y + th/2)) - (h/2 + th/2));
      const d  = Math.sqrt(dx*dx + dy*dy);
      if (d < minDist) minDist = d;
    }
    // Distance aux murs
    const dWallL = Math.max(0, curX - roomX0);
    const dWallR = Math.max(0, roomX0 + roomPxW - curX - w);
    const dWallT = Math.max(0, curY - roomY0);
    const dWallB = Math.max(0, roomY0 + roomPxH - curY - h);
    const dWall  = Math.min(dWallL, dWallR, dWallT, dWallB);
    return { nearest: minDist, wall: dWall };
  }

  // ── Drag ─────────────────────────────────────────────────────────────────
  const onPointerDown = useCallback((e) => {
    if (e.target.closest('[data-seat]')) return;
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = {
      active: false,
      startPx: e.clientX, startPy: e.clientY,
      elemX: table.x, elemY: table.y,
      pointerId: e.pointerId,
    };
  }, [table.x, table.y]);

  const onPointerMove = useCallback((e) => {
    if (!drag.current.startPx) return;
    const zoom = parseFloat(
      document.getElementById('pdt-canvas-inner')?.style.transform?.match(/scale\(([^)]+)\)/)?.[1] || 1
    );
    const dx = (e.clientX - drag.current.startPx) / zoom;
    const dy = (e.clientY - drag.current.startPy) / zoom;

    if (!drag.current.active) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      drag.current.active = true;
    }

    const rawX  = drag.current.elemX + dx;
    const rawY  = drag.current.elemY + dy;
    // Snap à la grille
    const newX  = snapPx(rawX);
    const newY  = snapPx(rawY);
    setOptimisticPos(table.id, newX, newY);

    // Distance en temps réel
    if (showDistances) {
      const { nearest, wall } = computeNearestDist(newX, newY);
      setNearestDist({ nearest: pxToM(nearest, pxPerM), wall: pxToM(wall, pxPerM) });
    }
  // eslint-disable-next-line
  }, [table.id, snapPx, setOptimisticPos, showDistances, pxPerM]);

  const onPointerUp = useCallback((e) => {
    if (!drag.current.startPx) return;
    if (drag.current.active) {
      const zoom = parseFloat(
        document.getElementById('pdt-canvas-inner')?.style.transform?.match(/scale\(([^)]+)\)/)?.[1] || 1
      );
      const dx = (e.clientX - drag.current.startPx) / zoom;
      const dy = (e.clientY - drag.current.startPy) / zoom;
      const rawX  = drag.current.elemX + dx;
      const rawY  = drag.current.elemY + dy;
      const newX  = snapPx(rawX);
      const newY  = snapPx(rawY);
      commitPosition(table.id, newX, newY);
      setNearestDist(null);
    } else {
      // Simple clic
      if (assignMode) {
        const seated = invites.filter(i => i.table_id === table.id);
        const seats  = computeSeatPositions(table.type, w, h, table.capacity || 0);
        const freeIdx = seats.findIndex(s => !seated.find(i => i.seat_idx === s.idx));
        if (freeIdx >= 0) assignSeat(assignMode, table.id, seats[freeIdx].idx);
      } else {
        setSelectedId(prev => prev === table.id ? null : table.id);
      }
    }
    drag.current = { active: false };
  // eslint-disable-next-line
  }, [table.id, assignMode, invites, w, h, table.capacity, table.type, commitPosition, snapPx]);

  const seated   = invites.filter(i => i.table_id === table.id);
  const seats    = SITABLE_TYPES.has(table.type)
    ? computeSeatPositions(table.type, w, h, table.capacity || 0) : [];
  const isDecor  = !SITABLE_TYPES.has(table.type);

  // Dimensions en mètres pour l'étiquette
  const wLabel = (w / pxPerM).toFixed(1);
  const hLabel = (h / pxPerM).toFixed(1);
  const sameWH = wLabel === hLabel;

  return (
    <div
      style={{
        ...tableStyle(table.type, w, h, selected, brandColor),
        left: table.x, top: table.y,
        transform: `rotate(${rotation}deg)`,
        transformOrigin: `${w/2}px ${h/2}px`,
        zIndex: selected ? 20 : 10,
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* Label table */}
      <span style={{
        fontFamily: "'Montserrat',sans-serif", fontWeight: 700,
        fontSize: Math.max(8, Math.min(13, w * 0.12)),
        color: isDecor ? '#5DBCBF' : '#0B1E20',
        textAlign: 'center', padding: '0 4px',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        maxWidth: '90%', pointerEvents: 'none', lineHeight: 1.2,
      }}>
        {table.type === 'honor' && '👑 '}{table.label || table.type}
      </span>

      {/* Dimensions en mètres */}
      <span style={{
        fontFamily: "'Montserrat',sans-serif", fontWeight: 400,
        fontSize: Math.max(7, Math.min(9, w * 0.1)),
        color: 'rgba(0,0,0,0.4)', pointerEvents: 'none',
        lineHeight: 1.2,
      }}>
        {sameWH ? `Ø${wLabel}m` : `${wLabel}×${hLabel}m`}
      </span>

      {/* Compteur sièges */}
      {seats.length > 0 && (
        <span style={{
          fontSize: Math.max(7, Math.min(9, w * 0.09)),
          color: 'rgba(0,0,0,0.35)', pointerEvents: 'none', lineHeight: 1,
        }}>
          {seated.length}/{table.capacity || 0}
        </span>
      )}

      {/* Icône décor */}
      {isDecor && DECOR_ICONS[table.type]}

      {/* Sièges */}
      {seats.map(pos => {
        const occupant = seated.find(i => i.seat_idx === pos.idx);
        const group    = occupant ? (planData?.groups || []).find(g => g.id === occupant.group_id) : null;
        const pulsing  = !!assignMode && !occupant;
        return (
          <Seat
            key={pos.idx}
            position={pos}
            idx={pos.idx}
            occupant={occupant}
            group={group}
            pulsing={pulsing}
            onClick={(idx) => {
              if (assignMode && !occupant) {
                assignSeat(assignMode, table.id, idx);
              } else if (occupant) {
                unassignSeat(occupant.id);
              }
            }}
          />
        );
      })}

      {/* Bulle distance (drag) */}
      {nearestDist && showDistances && (
        <div style={{
          position: 'absolute', top: -28, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.75)', color: '#fff',
          fontSize: 9, fontWeight: 700, padding: '3px 7px', borderRadius: 10,
          whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 50,
          fontFamily: "'Montserrat',sans-serif",
        }}>
          {nearestDist.nearest < 50
            ? `↔ ${nearestDist.nearest.toFixed(2)}m · mur ${nearestDist.wall.toFixed(2)}m`
            : `mur ${nearestDist.wall.toFixed(2)}m`
          }
        </div>
      )}
    </div>
  );
}
